/**
 * Utility functions for ArithmeticExpressionInput component
 * Handles arithmetic expression building, parsing, and validation for NumberInput dimensions
 */

/**
 * Build expression dimension value from data fields and aggregate expression
 * @param {Object} dataFields - { A: fieldObj, B: fieldObj, ... }
 * @param {String} aggregateExpression - "A + B - C" or generated from preset type
 * @param {String} aggregateType - "sumAll" | "subtractAll" | "custom"
 * @returns {Object} Dimension value with isExpression: true
 */
export const buildExpressionValue = (dataFields, aggregateExpression, aggregateType) => {
  const fieldEntries = Object.entries(dataFields).filter(([_, field]) => field != null);

  if (fieldEntries.length < 2) {
    // Not enough fields for an expression, return single field
    return dataFields.A;
  }

  // Resolve expression to field paths
  const resolvedValuePath = resolveExpressionToFieldPaths(aggregateExpression, dataFields);

  // Build display from field labels
  const displayParts = aggregateExpression.split(/([+-])/).map(part => {
    const trimmed = part.trim();
    if (trimmed === '+' || trimmed === '-') {
      return ` ${trimmed} `;
    }
    const field = dataFields[trimmed];
    return field?.display || field?.props?.label || trimmed;
  });
  const display = displayParts.join('');

  // Get content type from field A (all fields must be same content type)
  const contentType = dataFields.A?.contentType;

  // Build unique value identifier
  const expressionId = aggregateExpression.replace(/\s/g, '');
  const value = `${contentType}__expr_${expressionId}`;

  return {
    display,
    value,
    valuePath: resolvedValuePath,
    isExpression: true,
    comp: 'NumberInput',
    contentType,
    dataFields: { ...dataFields },
    aggregateExpression,
    aggregateType
  };
};

/**
 * Parse dimension value back to component state
 * @param {Object} value - Dimension value (may be expression or single field)
 * @returns {Object} { dataFields, aggregateExpression, aggregateType }
 */
export const parseExpressionValue = (value) => {
  if (!value) {
    return {
      dataFields: { A: null },
      aggregateExpression: '',
      aggregateType: null
    };
  }

  // Check if this is an expression value
  if (value.isExpression && value.dataFields) {
    return {
      dataFields: value.dataFields,
      aggregateExpression: value.aggregateExpression || '',
      aggregateType: value.aggregateType || 'custom'
    };
  }

  // Single field value
  return {
    dataFields: { A: value },
    aggregateExpression: '',
    aggregateType: null
  };
};

/**
 * Convert letter-based expression to actual field paths
 * @param {String} expression - Expression with letters (e.g., "A + B - C")
 * @param {Object} dataFields - { A: fieldObj, B: fieldObj, ... }
 * @returns {String} Expression with field paths (e.g., "main.a + main.b - main.c")
 */
export const resolveExpressionToFieldPaths = (expression, dataFields) => {
  if (!expression || !dataFields) return '';

  let result = expression;

  // Sort letters by length descending to avoid partial replacements (e.g., AA before A)
  const letters = Object.keys(dataFields).sort((a, b) => b.length - a.length);

  for (const letter of letters) {
    const field = dataFields[letter];
    if (field?.valuePath) {
      // Use word boundary regex to replace only complete letter matches
      const regex = new RegExp(`\\b${letter}\\b`, 'g');
      result = result.replace(regex, field.valuePath);
    }
  }

  return result;
};

/**
 * Validate custom expression syntax
 * @param {String} expression - User-typed expression
 * @param {Object} dataFields - Available data fields
 * @returns {Object} { valid: boolean, error: string|null }
 */
export const validateCustomExpression = (expression, dataFields) => {
  if (!expression || !expression.trim()) {
    return { valid: false, error: 'Expression cannot be empty' };
  }

  const trimmed = expression.trim();

  // Check for parentheses (not supported)
  if (trimmed.includes('(') || trimmed.includes(')')) {
    return { valid: false, error: 'Parentheses are not supported' };
  }

  // Check for invalid characters (only allow letters A-Z, +, -, spaces)
  const invalidChars = trimmed.replace(/[A-Z+\-\s]/g, '');
  if (invalidChars.length > 0) {
    return { valid: false, error: `Invalid characters: ${invalidChars}` };
  }

  // Extract all letters from expression
  const lettersInExpression = trimmed.match(/[A-Z]+/g) || [];

  if (lettersInExpression.length === 0) {
    return { valid: false, error: 'Expression must contain at least one field reference' };
  }

  // Check that all referenced letters exist in dataFields
  const availableLetters = Object.keys(dataFields).filter(k => dataFields[k] != null);
  for (const letter of lettersInExpression) {
    if (!availableLetters.includes(letter)) {
      return { valid: false, error: `Field "${letter}" is not defined` };
    }
  }

  // Check expression structure (should be: LETTER [OP LETTER]*)
  const parts = trimmed.split(/\s+/).filter(p => p);
  for (let i = 0; i < parts.length; i++) {
    const isOperatorPosition = i % 2 === 1;
    const part = parts[i];

    if (isOperatorPosition) {
      if (part !== '+' && part !== '-') {
        return { valid: false, error: `Expected operator at position ${i + 1}, got "${part}"` };
      }
    } else {
      if (!/^[A-Z]+$/.test(part)) {
        return { valid: false, error: `Expected field letter at position ${i + 1}, got "${part}"` };
      }
    }
  }

  // Expression should end with a field, not an operator
  if (parts.length > 0 && (parts[parts.length - 1] === '+' || parts[parts.length - 1] === '-')) {
    return { valid: false, error: 'Expression cannot end with an operator' };
  }

  return { valid: true, error: null };
};

/**
 * Filter options to only direct NumberInput fields (excludes rollup fields)
 * Handles both grouped options ({ display, options: [...] }) and flat arrays
 * @param {Array} options - Options from useGetAllOptionsForDimension1 (grouped) or flat array
 * @param {String} contentType - Content type filter (required for B, C, D fields)
 * @returns {Array} Filtered options containing only direct NumberInput (excludes rollup)
 */
export const filterNumberInputOptions = (options, contentType) => {
  if (!options || !Array.isArray(options)) return [];

  // Check if options are grouped (have nested options array) or flat
  const isGrouped = options.some(item => item.options && Array.isArray(item.options));

  if (isGrouped) {
    // Handle grouped options: { display, options: [...] }
    return options
      .map(group => {
        if (!group.options || !Array.isArray(group.options)) {
          return null;
        }

        const filteredOptions = group.options.filter(opt =>
          opt.comp === 'NumberInput' &&
          !opt.rollupPath &&
          opt.contentType === contentType
        );

        if (filteredOptions.length === 0) {
          return null;
        }

        return {
          ...group,
          options: filteredOptions
        };
      })
      .filter(Boolean);
  } else {
    // Handle flat options array (e.g., from MetricCard)
    const filteredOptions = options.filter(opt =>
      opt.comp === 'NumberInput' &&
      !opt.rollupPath &&
      opt.contentType === contentType
    );

    // Return as flat array (same format as input)
    return filteredOptions;
  }
};

/**
 * Get next available letter for new field
 * @param {Object} dataFields - Current data fields
 * @returns {String} Next letter ("B", "C", etc.)
 */
export const getNextLetter = (dataFields) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const existingLetters = Object.keys(dataFields);

  for (let i = 0; i < alphabet.length; i++) {
    const letter = alphabet[i];
    if (!existingLetters.includes(letter)) {
      return letter;
    }
  }

  // Fallback (shouldn't happen with 26 letters)
  return `Z${existingLetters.length}`;
};

/**
 * Generate expression for preset types
 * @param {String} type - "sumAll" | "subtractAll" | "subtractAllReverse"
 * @param {Object} dataFields - { A: ..., B: ..., C: ... }
 * @returns {String} Expression like "A + B + C" or "A - B - C" or "C - B - A"
 */
export const generatePresetExpression = (type, dataFields) => {
  const letters = Object.keys(dataFields)
    .filter(k => dataFields[k] != null)
    .sort();

  if (letters.length === 0) return '';
  if (letters.length === 1) return letters[0];

  if (type === 'sumAll') {
    return letters.join(' + ');
  } else if (type === 'subtractAll') {
    return letters.join(' - ');
  } else if (type === 'subtractAllReverse') {
    // Reverse the letters: N - ... - B - A
    return [...letters].reverse().join(' - ');
  }

  // Default fallback
  return letters.join(' + ');
};

/**
 * Check if a field can be used in arithmetic expressions
 * Must be a direct NumberInput field (no rollup)
 * @param {Object} field - Field object
 * @returns {Boolean}
 */
export const canFieldBeUsedInExpression = (field) => {
  if (!field) return false;
  return field.comp === 'NumberInput' && !field.rollupPath;
};

/**
 * Reletter data fields after removal to maintain contiguous lettering
 * @param {Object} dataFields - Current data fields (may have gaps)
 * @returns {Object} Relettered data fields with contiguous letters
 */
export const reletterDataFields = (dataFields) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const sortedEntries = Object.entries(dataFields)
    .filter(([_, field]) => field != null)
    .sort(([a], [b]) => a.localeCompare(b));

  const result = {};
  sortedEntries.forEach(([_, field], index) => {
    result[alphabet[index]] = field;
  });

  return result;
};
